import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { CustomLoader } from "../Services/CommonLibrary";
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';

import CommunicationText from './CommunicationText';
import CommunicationAudio from './CommunicationAudio';
import CommunicationWhatsapp from './CommunicationWhatsapp';

import { CommunicationCallDetail, serviceCallData, CommunicationSmsDetail, serviceSmsData, CommunicationWhatsappDetail, serviceWhatsappData } from '../Services/Service';
import { formatUkNumber } from "./Utils";

import StatCard from "../WebPages/CommunicationCenter/components/StatCard";
import CommunicationModal from "../WebPages/CommunicationCenter/components/CommunicationModal";

import { defaultOneMonthDate } from "../Services/commonFun";
import FilterComponent from '../WebPages/super-overview/components/FilterComponent';

// Default call data
const DEFAULT_CALL_DATA = {
    scheduled: 0,
    completed: 0,
    incomplete: 0,
    missed: 0,
    cancelled: 0,
    unsuccessfull: 0
};

// Default SMS data
const DEFAULT_SMS_DATA = {
    smsSent: 0,
    smsDelivered: 0,
    smsUnDelivered: 0
};

// Default Whatsapp data
const DEFAULT_WHATSAPP_DATA = {
    whatsappSent: 0,
    whatsappDelivered: 0,
    whatsappUnDelivered: 0,
    whatsappDataRead: 0,
    whatsappOptOut: 0
};

// Helper function to extract call data from API response
const extractCallData = (apiCallData) => ({
    scheduled: apiCallData?.scheduled || 0,
    completed: apiCallData?.completed || 0,
    incomplete: apiCallData?.incomplete || 0,
    missed: apiCallData?.missed || 0,
    cancelled: apiCallData?.cancelled || 0,
    unsuccessfull: apiCallData?.unsuccessful || 0
});

// extract SMS data
const extractSmsData = (apiSmsData) => ({
    smsSent: apiSmsData?.smsSent || apiSmsData?.smsSent || 0,
    smsDelivered: apiSmsData?.smsDelivered || apiSmsData?.smsDelivered || 0,
    smsUnDelivered: apiSmsData?.smsUnDelivered || apiSmsData?.smsUnDelivered || 0
});

// extract SMS data
const extractWhatsappData = (apiWhatsappData) => ({
    delivered: apiWhatsappData?.delivered || 0,
    undelivered: apiWhatsappData?.undelivered || 0,
    sent: apiWhatsappData?.sent || 0,
    read: apiWhatsappData?.readed || 0,
    optOut: apiWhatsappData?.optedOut || 0
});

function Communication() {
    const showWhatsapp = process.env.REACT_APP_SHOW_WHATSAPP_COMMUNICATION === 'true';
    console.log("showWhatsapp", process.env);
    const [key, setKey] = useState('audio');
    const [loading, setLoading] = useState(false);
    const [callDetailsLoading, setCallDetailsLoading] = useState(false);
    const [callData, setCallData] = useState(DEFAULT_CALL_DATA);
    const [smsData, setSmsData] = useState(DEFAULT_SMS_DATA);
    const [whatsappData, setWhatsappData] = useState(DEFAULT_WHATSAPP_DATA);
    // Payload defaults
    const { today, lastMonth } = defaultOneMonthDate();

    // MODAL STATES
    const [showModal, setShowModal] = useState(false);
    const [modalTitle, setModalTitle] = useState("");
    const [modalList, setModalList] = useState([]);
    const [modalLoading, setModalLoading] = useState(false);
    const [modalHeaders, setModalHeaders] = useState(["Year", "Month/Week/Date", "Conversation Count"]);
    // Filter form state (used by FilterComponent)
    const [filterForm, setFilterForm] = useState({});
    
    // Pagination state for modal
    const [modalPage, setModalPage] = useState(1);
    const [modalPageSize, setModalPageSize] = useState(10);
    const [modalTotalPages, setModalTotalPages] = useState(1);
    const [modalTotalRecords, setModalTotalRecords] = useState(0);
    const [modalDataApi, setModalDataApi] = useState(""); // Track which API is being used

    // Helper function to call API and update call data
    const callApiAndUpdateData = useCallback(async (start, end, filters, program) => {
        setCallDetailsLoading(true);
        try {
            const callsData = await CommunicationCallDetail(start, end, filters, program);
            const smsData = await CommunicationSmsDetail(start, end, filters, program);
            const whatsappData = await CommunicationWhatsappDetail(start, end, filters, program);
            setCallData(extractCallData(callsData.data.callData));
            setSmsData(extractSmsData(smsData.data.data.smsStatusCounts));
            setWhatsappData(extractWhatsappData(whatsappData.data.data));
        } catch (err) {
            console.error('CommunicationCallDetail error', err);
        } finally {
            setCallDetailsLoading(false);
        }
    }, []);

    // Fetch initial data
    useEffect(() => {
        callApiAndUpdateData(lastMonth, today, {}, "");
    }, [callApiAndUpdateData, lastMonth, today]);

    // Function to fetch modal data with pagination
    const fetchModalData = useCallback(async (title, apiCalls, page = 1, pageSize = 10) => {
        setModalLoading(true);
        setModalTitle(title);
        setModalDataApi(apiCalls);
        
        try {
            let response;
            
            if (apiCalls === "callsData") {
                setModalHeaders(["Year", "Month/Week/Date", "Conversation Count"]);
                response = await serviceCallData(
                    filterForm && Object.prototype.hasOwnProperty.call(filterForm, 'startRange') ? filterForm.startRange : lastMonth,
                    filterForm && Object.prototype.hasOwnProperty.call(filterForm, 'endRange') ? filterForm.endRange : today,
                    title,
                    filterForm && Object.prototype.hasOwnProperty.call(filterForm, 'divisionClinicFilter') ? filterForm.divisionClinicFilter : {},
                    filterForm && Object.prototype.hasOwnProperty.call(filterForm, 'programSelect') ? filterForm.programSelect : "",
                    page,
                    pageSize
                );
                
            } else if (apiCalls === "smssData") {
                setModalHeaders(["Sr No", "Mobile No", "Text", "Status", "Sent Date"]);
                response = await serviceSmsData(
                    filterForm && Object.prototype.hasOwnProperty.call(filterForm, 'startRange') ? filterForm.startRange : lastMonth,
                    filterForm && Object.prototype.hasOwnProperty.call(filterForm, 'endRange') ? filterForm.endRange : today,
                    title,
                    filterForm && Object.prototype.hasOwnProperty.call(filterForm, 'divisionClinicFilter') ? filterForm.divisionClinicFilter : {},
                    filterForm && Object.prototype.hasOwnProperty.call(filterForm, 'programSelect') ? filterForm.programSelect : "",
                    page,
                    pageSize
                );
                
            } else if (apiCalls === "whatsappsData") {
                setModalHeaders(["Sr No", "Mobile No", "Text", "Status", "Sent Date"]);
                response = await serviceWhatsappData(
                    filterForm && Object.prototype.hasOwnProperty.call(filterForm, 'startRange') ? filterForm.startRange : lastMonth,
                    filterForm && Object.prototype.hasOwnProperty.call(filterForm, 'endRange') ? filterForm.endRange : today,
                    title,
                    filterForm && Object.prototype.hasOwnProperty.call(filterForm, 'divisionClinicFilter') ? filterForm.divisionClinicFilter : {},
                    filterForm && Object.prototype.hasOwnProperty.call(filterForm, 'programSelect') ? filterForm.programSelect : "",
                    page,
                    pageSize
                );
            }
            
            if (response?.data) {
                setModalList(response.data);
                setModalTotalPages(response.data.totalPages || 1);
                setModalTotalRecords(response.data.total || 0);
                setModalPage(page);
                setModalPageSize(pageSize);
            }
        } catch (err) {
            console.error('Error fetching modal data:', err);
            setModalList({ message: 'Failed to load data' });
        } finally {
            setModalLoading(false);
        }
    }, [filterForm, lastMonth, today]);

    // Open modal with initial data
    const openModalWithData = useCallback(async (title, apiCalls) => {
        if (modalLoading) return;
        
        setShowModal(true);
        
        // Fetch first page of data
        await fetchModalData(title, apiCalls, 1, 10);
        
    }, [modalLoading, fetchModalData]);

    // Handle page change from modal
    const handleModalPageChange = useCallback((page) => {
        setModalPage(page);
        fetchModalData(modalTitle, modalDataApi, page, modalPageSize);
    }, [fetchModalData, modalTitle, modalDataApi, modalPageSize]);

    // Handle page size change from modal
    const handleModalPageSizeChange = useCallback((pageSize) => {
        setModalPageSize(pageSize);
        setModalPage(1); // Reset to first page when changing page size
        fetchModalData(modalTitle, modalDataApi, 1, pageSize);
    }, [fetchModalData, modalTitle, modalDataApi]);

    // Handle modal close
    const handleModalClose = useCallback(() => {
        setShowModal(false);
        setModalList([]);
        setModalPage(1);
        setModalPageSize(10);
        setModalTotalPages(1);
        setModalTotalRecords(0);
        setModalDataApi("");
        setModalTitle("");
    }, []);

    const clearForm = async () => {
        setLoading(true);
        try {
            // Call API with empty/cleared params; filtering is handled by `FilterComponent` now
            await callApiAndUpdateData("", "", {}, "");
        } catch (err) {
            console.error('Clear form error', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <section className='projects-section uploadtab call-communications'>
                <FilterComponent
                    title="Communication Center"
                    onGo={(data) => {
                        setFilterForm((prev) => ({ ...prev, ...data }));
                        const s = data && Object.prototype.hasOwnProperty.call(data, 'startRange') ? data.startRange : lastMonth;
                        const e = data && Object.prototype.hasOwnProperty.call(data, 'endRange') ? data.endRange : today;
                        const filters = data && Object.prototype.hasOwnProperty.call(data, 'divisionClinicFilter') ? data.divisionClinicFilter : {};
                        const program = data && Object.prototype.hasOwnProperty.call(data, 'programSelect') ? data.programSelect : "";

                        callApiAndUpdateData(s, e, filters, program);
                    }}
                />

                <div className="section-subtitle mb-3 mt-5">
                    {
                    !showWhatsapp ? null : <><h2 className="title">Whatsapp Breakdown</h2>
                    <div className="row mb-5">
                        <StatCard
                            title="Sent"
                            subtitle="Sent"
                            imgSrc="https://appcdn.goqii.com/storeimg/26476_1731930269.png"
                            value={formatUkNumber(whatsappData.sent)}
                            isLoading={callDetailsLoading}
                            onClick={() => openModalWithData("Whatsapp Sent", "whatsappsData")}
                        />

                        <StatCard
                            title="Delivered"
                            subtitle="Delivered"
                            imgSrc="https://appcdn.goqii.com/storeimg/62955_1731930583.png"
                            value={formatUkNumber(whatsappData.delivered)}
                            isLoading={callDetailsLoading}
                            onClick={() => openModalWithData("Whatsapp Delivered", "whatsappsData")}
                        />

                        <StatCard
                            title="Undelivered"
                            subtitle="Undelivered"
                            imgSrc="https://appcdn.goqii.com/storeimg/20839_1731931059.png"
                            value={formatUkNumber(whatsappData.undelivered)}
                            isLoading={callDetailsLoading}
                            onClick={() => openModalWithData("Whatsapp Undelivered", "whatsappsData")}
                        />

                        <StatCard
                            title="Read"
                            subtitle="Read"
                            imgSrc="https://appcdn.goqii.com/storeimg/20839_1731931059.png"
                            value={formatUkNumber(whatsappData.read)}
                            isLoading={callDetailsLoading}
                            onClick={() => openModalWithData("Whatsapp Read", "whatsappsData")}
                        />

                        <StatCard
                            title="Opt-Out"
                            subtitle="Opt-Out"
                            imgSrc="https://appcdn.goqii.com/storeimg/20839_1731931059.png"
                            value={formatUkNumber(whatsappData.optOut)}
                            isLoading={callDetailsLoading}
                            onClick={() => openModalWithData("Whatsapp Opt-Out", "whatsappsData")}
                        />
                    </div>
                    </>
                    }

                    <h2 className="title">SMS Breakdown</h2>
                    <div className="row mb-5">
                        <StatCard
                            title="Sent"
                            subtitle="Sent"
                            imgSrc="https://appcdn.goqii.com/storeimg/26476_1731930269.png"
                            value={formatUkNumber(smsData.smsSent)}
                            isLoading={callDetailsLoading}
                            onClick={() => openModalWithData("SMS Sent", "smssData")}
                        />

                        <StatCard
                            title="Delivered"
                            subtitle="Delivered"
                            imgSrc="https://appcdn.goqii.com/storeimg/62955_1731930583.png"
                            value={formatUkNumber(smsData.smsDelivered)}
                            isLoading={callDetailsLoading}
                            onClick={() => openModalWithData("SMS Delivered", "smssData")}
                        />

                        <StatCard
                            title="Undelivered"
                            subtitle="Undelivered"
                            imgSrc="https://appcdn.goqii.com/storeimg/20839_1731931059.png"
                            value={formatUkNumber(smsData.smsUnDelivered)}
                            isLoading={callDetailsLoading}
                            onClick={() => openModalWithData("SMS Undelivered", "smssData")}
                        />
                    </div>

                    <h2 className="title">Call Breakdown</h2>
                    <div className="row mb-5">
                        <StatCard
                            title="Scheduled Calls"
                            subtitle="Scheduled Calls"
                            imgSrc="https://appcdn.goqii.com/storeimg/26476_1731930269.png"
                            value={formatUkNumber(callData.scheduled)}
                            isLoading={callDetailsLoading}
                            onClick={() => openModalWithData("Scheduled Calls", "callsData")}
                        />

                        <StatCard
                            title="Completed Calls"
                            subtitle="Completed Calls"
                            imgSrc="https://appcdn.goqii.com/storeimg/62955_1731930583.png"
                            value={formatUkNumber(callData.completed)}
                            isLoading={callDetailsLoading}
                            onClick={() => openModalWithData("Completed Calls", "callsData")}
                        />

                        <StatCard
                            title="Incomplete Calls"
                            subtitle="Incomplete Calls"
                            imgSrc="https://appcdn.goqii.com/storeimg/20839_1731931059.png"
                            value={formatUkNumber(callData.incomplete)}
                            isLoading={callDetailsLoading}
                            onClick={() => openModalWithData("Incomplete Calls", "callsData")}
                        />

                        <StatCard
                            title="Missed Calls"
                            subtitle="Missed Calls"
                            imgSrc="https://appcdn.goqii.com/storeimg/46802_1731931316.png"
                            value={formatUkNumber(callData.missed)}
                            isLoading={callDetailsLoading}
                            onClick={() => openModalWithData("Missed Calls", "callsData")}
                        />

                        <StatCard
                            title="Cancelled Calls"
                            subtitle="Cancelled Calls"
                            imgSrc="https://appcdn.goqii.com/storeimg/65691_1731930796.png"
                            value={formatUkNumber(callData.cancelled)}
                            isLoading={callDetailsLoading}
                            onClick={() => openModalWithData("Cancelled Calls", "callsData")}
                        />

                        <StatCard
                            title="Unsuccessful Calls"
                            subtitle="Unsuccessful Calls"
                            imgSrc="https://appcdn.goqii.com/storeimg/79726_1731931469.png"
                            value={formatUkNumber(callData.unsuccessfull)}
                            isLoading={callDetailsLoading}
                            onClick={() => openModalWithData("Unsuccessful Calls", "callsData")}
                        />
                    </div>
                </div>

                <Tabs id="controlled-tab-example" activeKey={key} onSelect={(k) => setKey(k)} className="mb-1">
                    <Tab eventKey="audio" title="Call communications">
                        <CommunicationAudio />
                    </Tab>
                    <Tab eventKey="text" title="Text communications">
                        <CommunicationText />
                    </Tab>
                    {!showWhatsapp ? null : <Tab eventKey="whatsapp" title="Whatsapp communications">
                        <CommunicationWhatsapp />
                    </Tab>}
                </Tabs>
            </section>

            {loading && <CustomLoader />}

            <CommunicationModal
                show={showModal}
                title={modalTitle}
                loading={modalLoading}
                list={modalList}
                onClose={handleModalClose}
                customHeaders={modalHeaders}
                filterForm={filterForm}
                currentPage={modalPage}
                totalPages={modalTotalPages}
                totalRecords={modalTotalRecords}
                rowsPerPage={modalPageSize}
                onPageChange={handleModalPageChange}
                onRowsPerPageChange={handleModalPageSizeChange}
                modalDataApi={modalDataApi}
                lastMonth={lastMonth}
                today={today}
            />
        </>
    );
}

export default Communication;